import { Injectable, computed, effect, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'sd-theme-mode';
const DARK_THEME_COLOR = '#060F1E';
const DEFAULT_LIGHT_THEME_COLOR = '#2E6BFF';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _mode = signal<ThemeMode>(this.detectInitialMode());
  private readonly _tenantPrimary = signal<string | null>(null);

  readonly mode = this._mode.asReadonly();
  readonly isDark = computed(() => this._mode() === 'dark');

  constructor() {
    effect(() => {
      const value = this._mode();
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        /* localStorage unavailable — ignore */
      }
    });

    effect(() => {
      this.applyThemeColorMeta(this._mode(), this._tenantPrimary());
    });

    effect(() => {
      this.applyDocumentTheme(this._mode());
    });
  }

  private applyDocumentTheme(mode: ThemeMode): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', mode);
  }

  /**
   * Lets `TenantContextService` notify us when the tenant's branding primary color
   * is loaded so we can reflect it in the mobile status-bar (notch) when in light mode.
   * In dark mode we always force the dark surface color regardless of tenant branding.
   */
  setTenantPrimary(hex: string | null): void {
    this._tenantPrimary.set(hex);
  }

  private applyThemeColorMeta(mode: ThemeMode, tenantPrimary: string | null): void {
    if (typeof document === 'undefined') return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    if (mode === 'dark') {
      meta.content = DARK_THEME_COLOR;
    } else {
      meta.content = tenantPrimary || DEFAULT_LIGHT_THEME_COLOR;
    }
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
  }

  toggle(): void {
    this._mode.update((m) => (m === 'dark' ? 'light' : 'dark'));
  }

  private detectInitialMode(): ThemeMode {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      /* localStorage unavailable — fall through */
    }
    try {
      if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    } catch {
      /* matchMedia unavailable — fall through */
    }
    return 'light';
  }
}
